import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import { useCallback, useEffect, useState, useMemo } from "react";

import { LoadingSpinner } from "../../components/ui";
import Button from "../../components/ui/button/Button";
import { getErrorMessage } from "../../utils/axios.utils";
import { RootState, useAppSelector } from "../../redux/store";
import { FiCheck, FiDelete, FiEdit, FiTrash } from "react-icons/fi";
import { SubjectRegistration, ExamRegistration } from "../../types/types";
import { ExamRegistrationService } from "../../services/exam-reg.service";
import { SubjectRegistrationService } from "../../services/subject-reg.service";
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHeader,
} from "../../components/ui/table";

const SubjectRegistrations = () => {
  const navigate = useNavigate();
  const lang = localStorage.getItem("lang") || "en";
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAppSelector((state: RootState) => state.auth);
  const [subjectRegistrations, setSubjectRegistrations] = useState<
    SubjectRegistration[]
  >([]);
  const [editingRegistrations, setEditingRegistrations] = useState<{
    [key: number]: number;
  }>({});
  const [selectedRegistrations, setSelectedRegistrations] = useState<number[]>(
    []
  );
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [filterText, setFilterText] = useState<string>("");
  const [examRegistrations, setExamRegistrations] = useState<
    ExamRegistration[]
  >([]);
  const [selectedExamRegistrationId, setSelectedExamRegistrationId] = useState<
    number | null
  >(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [paginationMeta, setPaginationMeta] = useState<{
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number | null;
    to: number | null;
  } | null>(null);

  const fetchSubjectRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const response =
        await new SubjectRegistrationService().getOwnSubjectRegistrations(
          selectedExamRegistrationId || undefined,
          limit,
          currentPage
        );

      setSubjectRegistrations(response.data);
      if (response.pagination) {
        setPaginationMeta(response.pagination);
      }
    } catch (error) {
      console.log(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [selectedExamRegistrationId, limit, currentPage]);

  const deleteSubjectRegistration = async (id: number) => {
    if (confirm("Are you sure you want to delete this registration?")) {
      try {
        await new SubjectRegistrationService().deleteSubjectRegistration(id);

        toast.success("Registration deleted successfully");

        fetchSubjectRegistrations();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const deleteSelectedRegistrations = async () => {
    if (selectedRegistrations.length === 0) {
      toast.error("Please select at least one registration to delete");
      return;
    }

    if (
      confirm(
        `Are you sure you want to delete ${selectedRegistrations.length} registration(s)?`
      )
    ) {
      try {
        await new SubjectRegistrationService().deleteSubjectRegistrations(
          selectedRegistrations
        );

        toast.success("Registrations deleted successfully");
        setSelectedRegistrations([]);
        fetchSubjectRegistrations();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const startEditing = (id: number, currentCount: number) => {
    setEditingRegistrations((prev) => ({
      ...prev,
      [id]: currentCount,
    }));
  };

  const cancelEditing = (id: number) => {
    setEditingRegistrations((prev) => {
      const newEditing = { ...prev };
      delete newEditing[id];
      return newEditing;
    });
  };

  const saveSingleRegistration = async (id: number) => {
    try {
      setSaving(true);
      const studentCount = editingRegistrations[id];

      if (studentCount < 1) {
        toast.error("Student count must be at least 1");
        return;
      }

      // Get the registration to check against total students
      const registration = subjectRegistrations.find((reg) => reg.id === id);
      if (registration && registration.exam_registration) {
        if (studentCount > registration.exam_registration.total_students) {
          toast.error(
            "Student count cannot exceed total students registered for the exam"
          );
          return;
        }
      }

      await new SubjectRegistrationService().updateSubjectRegistration(id, {
        student_count: studentCount,
      });

      toast.success("Registration updated successfully");
      cancelEditing(id);
      fetchSubjectRegistrations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const saveAllEditedRegistrations = async () => {
    const editedRegistrations = Object.entries(editingRegistrations).map(
      ([id, student_count]) => ({
        id: parseInt(id),
        student_count,
      })
    );

    if (editedRegistrations.length === 0) {
      toast.error("No registrations to save");
      return;
    }

    try {
      setSaving(true);

      // Validate each registration before saving
      for (const reg of editedRegistrations) {
        if (reg.student_count < 1) {
          toast.error(
            `Student count for registration ID ${reg.id} must be at least 1`
          );
          return;
        }

        // Get the registration to check against total students
        const registration = subjectRegistrations.find((r) => r.id === reg.id);
        if (registration && registration.exam_registration) {
          if (
            reg.student_count > registration.exam_registration.total_students
          ) {
            toast.error(
              `Student count for ${registration.subject?.code} cannot exceed total students registered for the exam`
            );
            return;
          }
        }
      }

      await new SubjectRegistrationService().updateSubjectRegistrations(
        editedRegistrations
      );

      toast.success("Registrations updated successfully");
      setEditingRegistrations({});
      fetchSubjectRegistrations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleStudentCountChange = (id: number, value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setEditingRegistrations((prev) => ({
        ...prev,
        [id]: numValue,
      }));
    }
  };

  const handleSelectRegistration = (id: number) => {
    setSelectedRegistrations((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRegistrations.length === filteredRegistrations.length) {
      setSelectedRegistrations([]);
    } else {
      setSelectedRegistrations(filteredRegistrations.map((reg) => reg.id));
    }
  };

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredRegistrations = useMemo(() => {
    let sortableItems = [...subjectRegistrations];

    // Apply filter
    if (filterText) {
      sortableItems = sortableItems.filter(
        (reg) =>
          reg.subject?.name?.toLowerCase().includes(filterText.toLowerCase()) ||
          reg.subject?.code?.toLowerCase().includes(filterText.toLowerCase()) ||
          reg.exam_registration?.exam_type?.name
            ?.toLowerCase()
            .includes(filterText.toLowerCase()) ||
          reg.exam_registration?.exam_session?.name
            ?.toLowerCase()
            .includes(filterText.toLowerCase())
      );
    }

    // Apply sorting
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "subjectName":
            aValue = a.subject?.name || "";
            bValue = b.subject?.name || "";
            break;
          case "subjectCode":
            aValue = a.subject?.code || "";
            bValue = b.subject?.code || "";
            break;
          case "examType":
            aValue = a.exam_registration?.exam_type?.name || "";
            bValue = b.exam_registration?.exam_type?.name || "";
            break;
          case "examSession":
            aValue = a.exam_registration?.exam_session?.name || "";
            bValue = b.exam_registration?.exam_session?.name || "";
            break;
          case "examCategory":
            aValue = a.exam_category?.name || "";
            bValue = b.exam_category?.name || "";
            break;
          case "studentCount":
            aValue = a.exam_registration?.total_students || 0;
            bValue = b.exam_registration?.total_students || 0;
            break;
          case "modifiedDate":
            aValue = new Date(a.updated_at).getTime();
            bValue = new Date(b.updated_at).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableItems;
  }, [subjectRegistrations, sortConfig, filterText]);

  const filteredRegistrations = sortedAndFilteredRegistrations;

  // Helper function to check if any selected registration belongs to completed payment
  const hasSelectedCompletedPayment = (): boolean => {
    return selectedRegistrations.some((id) => {
      const registration = subjectRegistrations.find((reg) => reg.id === id);
      return registration?.exam_registration?.payment_status === "completed";
    });
  };

  const fetchExamRegistrations = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await ExamRegistrationService.getOwnSchoolRegistrations(1, 100);
      const response: ExamRegistration[] = data;

      setExamRegistrations(response);
    } catch (error) {
      console.log(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    fetchExamRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSubjectRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExamRegistrationId, currentPage, limit]);

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-4 py-4 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Subject Registrations
        </h1>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => navigate(`/${lang}/${user?.role}/exams`)}
            variant="primary"
            size="md"
          >
            + New Registration
          </Button>
        </div>
      </div>

      {/* Batch Operations and Filters */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-center gap-2">
          <select
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            value={selectedExamRegistrationId || ""}
            onChange={(e) =>
              setSelectedExamRegistrationId(
                e.target.value ? parseInt(e.target.value) : null
              )
            }
          >
            <option value="">All Exam Registrations</option>
            {examRegistrations?.map((examReg) => (
              <option key={examReg.id} value={examReg.id}>
                {examReg.exam_type?.name} - {examReg.exam_session?.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Filter registrations..."
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          {selectedRegistrations.length > 0 && (
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {selectedRegistrations.length} selected
            </span>
          )}
          <Button
            variant="danger"
            size="sm"
            onClick={deleteSelectedRegistrations}
            disabled={
              selectedRegistrations.length === 0 ||
              saving ||
              hasSelectedCompletedPayment()
            }
          >
            Delete Selected
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={saveAllEditedRegistrations}
            disabled={
              Object.keys(editingRegistrations).length === 0 ||
              saving ||
              hasSelectedCompletedPayment()
            }
          >
            {saving ? "Saving..." : "Save All"}
          </Button>
        </div>
      </div>

      <div className="md:hidden space-y-4">
        {loading && <LoadingSpinner size="xl" />}
        {!loading && filteredRegistrations?.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-300">
            No Subject Registrations found for this school, please start a new
            registration.
          </div>
        ) : (
          filteredRegistrations?.map((subjectRegistration) => (
            <motion.div
              key={subjectRegistration.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white dark:bg-gray-800 p-4 rounded-xl shadow-md border ${selectedRegistrations.includes(subjectRegistration.id)
                  ? "border-blue-500 ring-1 ring-blue-500"
                  : "border-gray-200 dark:border-gray-700"
                }`}
            >
              {/* Header: Selection + Subject Name */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3 w-full">
                  <input
                    type="checkbox"
                    checked={selectedRegistrations.includes(
                      subjectRegistration.id
                    )}
                    onChange={() =>
                      handleSelectRegistration(subjectRegistration.id)
                    }
                    className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 dark:text-white truncate">
                      {subjectRegistration.subject?.name}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {subjectRegistration.subject?.code}
                      </span>
                      <span>•</span>
                      <span>{subjectRegistration.exam_registration?.exam_type?.name}</span>
                    </div>
                  </div>
                </div>

                {/* Delete Single Action */}
                <FiTrash
                  className="cursor-pointer text-red-500 hover:text-red-600 flex-shrink-0 ml-2"
                  size={18}
                  onClick={() =>
                    deleteSubjectRegistration(subjectRegistration.id)
                  }
                />
              </div>

              {/* Body: Details */}
              <div className="grid grid-cols-2 gap-4 text-sm mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 block text-xs mb-1">Students:</span>
                  {editingRegistrations[subjectRegistration.id] !== undefined ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={editingRegistrations[subjectRegistration.id]}
                        onChange={(e) =>
                          handleStudentCountChange(
                            subjectRegistration.id,
                            e.target.value
                          )
                        }
                        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white"
                      />
                      <FiCheck
                        size={18}
                        onClick={() =>
                          saveSingleRegistration(subjectRegistration.id)
                        }
                        className="cursor-pointer text-green-500"
                      />
                      <FiDelete
                        size={18}
                        onClick={() => cancelEditing(subjectRegistration.id)}
                        className="cursor-pointer text-red-500"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                      <span>
                        {subjectRegistration.exam_registration?.total_students}
                      </span>
                      {subjectRegistration.exam_registration?.payment_status !== "completed" && (
                        <FiEdit
                          size={16}
                          className="cursor-pointer text-blue-500"
                          onClick={() =>
                            startEditing(
                              subjectRegistration.id,
                              subjectRegistration.exam_registration?.total_students || 0
                            )
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400 block text-xs mb-1">Category:</span>
                  <span className="text-gray-900 dark:text-white font-medium truncate block">
                    {subjectRegistration.exam_category?.name || "N/A"}
                  </span>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
        {loading && <LoadingSpinner size="xl" />}
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-200 text-nowrap uppercase dark:bg-gray-700">
              <TableCell isHeader className="px-4 py-4 text-left">
                <input
                  type="checkbox"
                  checked={
                    selectedRegistrations.length ===
                    filteredRegistrations.length &&
                    filteredRegistrations.length > 0
                  }
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
              >
                <div onClick={() => handleSort("subjectName")}>
                  Subject Name
                  {sortConfig?.key === "subjectName" && (
                    <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
              >
                <div onClick={() => handleSort("subjectCode")}>
                  Subject Code
                  {sortConfig?.key === "subjectCode" && (
                    <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
              >
                <div onClick={() => handleSort("examType")}>
                  Exam Type
                  {sortConfig?.key === "examType" && (
                    <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer hidden md:table-cell"
              >
                <div onClick={() => handleSort("examSession")}>
                  Exam Session
                  {sortConfig?.key === "examSession" && (
                    <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer hidden md:table-cell"
              >
                <div onClick={() => handleSort("examCategory")}>
                  Exam Category
                  {sortConfig?.key === "examCategory" && (
                    <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
              >
                <div onClick={() => handleSort("studentCount")}>
                  Students Registered
                  {sortConfig?.key === "studentCount" && (
                    <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer hidden md:table-cell"
              >
                <div onClick={() => handleSort("modifiedDate")}>
                  Modified Date
                  {sortConfig?.key === "modifiedDate" && (
                    <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRegistrations?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-gray-500 dark:text-gray-300"
                >
                  No Subject Registrations found for this school, please start a
                  new registration.
                </TableCell>
              </TableRow>
            ) : (
              filteredRegistrations?.map((subjectRegistration) => (
                <motion.tr
                  key={subjectRegistration.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    <input
                      type="checkbox"
                      checked={selectedRegistrations.includes(
                        subjectRegistration.id
                      )}
                      onChange={() =>
                        handleSelectRegistration(subjectRegistration.id)
                      }
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {subjectRegistration.subject?.name}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {subjectRegistration.subject?.code}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {subjectRegistration.exam_registration?.exam_type?.name}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white hidden md:table-cell">
                    {subjectRegistration.exam_registration?.exam_session?.name}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white hidden md:table-cell">
                    {subjectRegistration.exam_category?.name || "N/A"}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {editingRegistrations[subjectRegistration.id] !==
                      undefined ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={editingRegistrations[subjectRegistration.id]}
                          onChange={(e) =>
                            handleStudentCountChange(
                              subjectRegistration.id,
                              e.target.value
                            )
                          }
                          className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <FiCheck
                          size={24}
                          onClick={() =>
                            saveSingleRegistration(subjectRegistration.id)
                          }
                          className="cursor-pointer text-green-500 hover:text-green-600"
                        />
                        <FiDelete
                          size={24}
                          onClick={() => cancelEditing(subjectRegistration.id)}
                          className="cursor-pointer text-red-500 hover:text-red-600"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>
                          {
                            subjectRegistration.exam_registration
                              ?.total_students
                          }
                        </span>
                        {subjectRegistration.exam_registration
                          ?.payment_status !== "completed" && (
                            <FiEdit
                              size={24}
                              className="cursor-pointer text-blue-500 hover:text-blue-600"
                              onClick={() =>
                                startEditing(
                                  subjectRegistration.id,
                                  subjectRegistration.exam_registration
                                    ?.total_students || 0
                                )
                              }
                            />
                          )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white hidden md:table-cell">
                    {subjectRegistration.updated_at}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 flex gap-2">
                    <FiTrash
                      className="cursor-pointer text-red-500 hover:text-red-600"
                      onClick={() =>
                        deleteSubjectRegistration(subjectRegistration.id)
                      }
                    />
                  </TableCell>
                </motion.tr>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {paginationMeta && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label
                htmlFor="limit-select"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Show:
              </label>
              <select
                id="limit-select"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Page {paginationMeta.current_page} of {paginationMeta.last_page}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={paginationMeta.current_page === 1 || loading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(paginationMeta.last_page, prev + 1)
                )
              }
              disabled={
                paginationMeta.current_page === paginationMeta.last_page ||
                loading
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectRegistrations;
